import { Controller } from 'stimulus';
import mapboxgl from 'mapbox-gl';
import * as turf from '@turf/turf';

export default class LocationFilterController extends Controller {
  static targets = [
    'myLocation',
    'differentLocation',
    'rangeInput',
    'latitude',
    'longitude',
    'mapContainer',
    'map',
  ];

  connect() {
    mapboxgl.accessToken = this.mapContainerTarget.dataset.mapboxKey;

    this.form = this.element.closest('form');
    this.form.addEventListener('reset', this._handleFormReset);

    this.rangeSlider = document.querySelector(
      '[name="filters[my_location_range]"]',
    );

    if (this.rangeSlider) {
      this.rangeSlider.addEventListener('input', this._handleRangeChange);
    }

    if (this.usingDifferentLocation) {
      this._checkLocationPermission();
      this._setupMap();
      this._showMap();
    } else {
      this._hideMap();
      this._getLocation();
    }
  }

  disconnect() {
    this.form.removeEventListener('reset', this._handleFormReset);

    if (this.rangeSlider) {
      this.rangeSlider.removeEventListener('input', this._handleRangeChange);
    }
  }

  sourceLocationChanged = ({ target }) => {
    if (target.value === 'different_location') {
      this._showMap();

      if (!this.map) {
        this._setupMap();
      } else {
        this.map.resize();
      }
    } else {
      this._hideMap();
      this._getLocation();
    }
  };

  _handleRangeChange = () => {
    this._updateCircle();
  };

  _handleFormReset = () => {
    requestAnimationFrame(() => {
      this._clearCoordinates();

      if (this.myLocationTarget.checked) {
        this._hideMap();
        this._getLocation();
      } else {
        this._updateCircle();
      }
    });
  };

  _getLocation() {
    this._clearCoordinates();

    if (!navigator.geolocation) {
      this._handleMyLocationError();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.element.classList.remove('disabled');
        this.myLocationTarget.disabled = false;

        this._setCoordinates(
          position.coords.latitude,
          position.coords.longitude,
        );
      },
      () => {
        this._handleMyLocationError();
      },
    );
  }

  _handleMyLocationError() {
    this.element.classList.add('disabled');
    this.myLocationTarget.disabled = true;
    this.differentLocationTarget.checked = true;
    this.sourceLocationChanged({ target: { value: 'different_location' } });
  }

  _checkLocationPermission() {
    if (!navigator.permissions) return;

    navigator.permissions
      .query({ name: 'geolocation' })
      .then((result) => {
        this._updateLocationPermissionState(result.state);

        result.onchange = () => {
          this._updateLocationPermissionState(result.state);
        };
      })
      .catch(() => {});
  }

  _updateLocationPermissionState(state) {
    const denied = state === 'denied';

    this.element.classList.toggle('disabled', denied);
    this.myLocationTarget.disabled = denied;
  }

  _setupMap(onReady) {
    const defaultCenter = [28.0473, -26.2041];

    const center = this._hasCoordinates()
      ? [this.longitude, this.latitude]
      : defaultCenter;

    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom: 11,
    });

    this.map.on('load', () => {
      this.map.resize();

      this.marker = new mapboxgl.Marker({ draggable: true })
        .setLngLat(center)
        .addTo(this.map);

      this.marker.on('dragend', () => {
        const pos = this.marker.getLngLat();
        this._setCoordinates(pos.lat, pos.lng);
        this._updateCircle();
      });

      this.map.on('click', (e) => {
        this._setMarker(e.lngLat.lng, e.lngLat.lat);
      });

      this._initCircleLayer();

      if (onReady) onReady();
    });
  }

  _setMarker(lng, lat) {
    if (!this.marker) return;

    this.marker.setLngLat([lng, lat]).addTo(this.map);

    this._setCoordinates(lat, lng);
    this._updateCircle();
  }

  _moveTo(lng, lat) {
    this.map.flyTo({
      center: [lng, lat],
      zoom: 11,
    });
  }

  _updateCircle() {
    if (!this.map || !this.marker) return;

    const pos = this.marker.getLngLat();

    const radius = parseFloat(this.rangeSlider?.value || 50); // km

    const circle = turf.circle([pos.lng, pos.lat], radius, {
      steps: 64,
      units: 'kilometers',
    });

    const source = this.map.getSource('radius');

    if (source) {
      source.setData(circle);
    }
  }

  _initCircleLayer() {
    const pos = this.marker.getLngLat();

    const radius = parseFloat(this.rangeSlider?.value || 50);

    const circle = turf.circle([pos.lng, pos.lat], radius, {
      steps: 64,
      units: 'kilometers',
    });

    this.map.addSource('radius', {
      type: 'geojson',
      data: circle,
    });

    this.map.addLayer({
      id: 'radius-fill',
      type: 'fill',
      source: 'radius',
      paint: {
        'fill-color': '#3b82f6',
        'fill-opacity': 0.2,
      },
    });

    this.map.addLayer({
      id: 'radius-outline',
      type: 'line',
      source: 'radius',
      paint: {
        'line-color': '#3b82f6',
        'line-width': 2,
      },
    });
  }

  _setCoordinates(lat, lng) {
    this.latitudeTarget.value = lat;
    this.longitudeTarget.value = lng;
  }

  _clearCoordinates() {
    this.latitudeTarget.value = '';
    this.longitudeTarget.value = '';
  }

  _hasCoordinates() {
    return this.latitudeTarget.value && this.longitudeTarget.value;
  }

  _showMap() {
    this.mapContainerTarget.classList.remove('hidden');
  }

  _hideMap() {
    this.mapContainerTarget.classList.add('hidden');
  }

  get latitude() {
    return parseFloat(this.latitudeTarget.value);
  }

  get longitude() {
    return parseFloat(this.longitudeTarget.value);
  }

  get usingDifferentLocation() {
    return (
      document.querySelector('[name="filters[location]"]:checked')?.value ===
      'different_location'
    );
  }
}
